"use client";

import React from "react";
import SectionHeading from "./section-heading";
import { motion } from "framer-motion";
import { useSectionInView } from "@/lib/hooks";
import { profile } from "@/lib/data";
import { FaWhatsapp, FaEnvelope, FaGithub, FaLinkedin } from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";

export default function Contact() {
  const { ref } = useSectionInView("Contact");

  const handleWhatsApp = () => {
    const whatsappNumber = profile.whatsapp.replace(/\+/g, "");
    window.open(`https://wa.me/${whatsappNumber}`, "_blank");
  };

  const handleEmail = () => {
    window.open(`mailto:${profile.email}`, "_blank");
  };

  return (
    <motion.section
      id="contact"
      ref={ref}
      className="mb-20 sm:mb-28 w-[min(100%,48rem)] text-center"
      initial={{
        opacity: 0,
      }}
      whileInView={{
        opacity: 1,
      }}
      transition={{
        duration: 1,
      }}
      viewport={{
        once: true,
      }}
    >
      <SectionHeading>Contact me</SectionHeading>

      <p className="text-white/80 -mt-6 mb-8">
        Feel free to reach out to me via WhatsApp or Email. I'm always open to discussing new projects, opportunities, or just having a chat!
      </p>

      {/* Social Links */}
      <div className="pb-10">
        <p className="text-sm text-gray-400 mb-4">
          Connect with me on social platforms
        </p>
        <div className="flex items-center justify-center gap-4">
          <motion.a
            href={profile.githubLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-gray-700 text-white px-6 py-3 rounded-xl hover:bg-gray-600 transition-all shadow-md hover:shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaGithub className="text-2xl group-hover:rotate-12 transition-transform" />
            <div className="flex flex-col items-start">
              <span className="text-xs text-gray-300">Follow me on</span>
              <span className="font-semibold">GitHub</span>
            </div>
          </motion.a>

          <motion.a
            href={profile.linkedInLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-blue-700 text-white px-6 py-3 rounded-xl hover:bg-blue-800 transition-all shadow-md hover:shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaLinkedin className="text-2xl group-hover:rotate-12 transition-transform" />
            <div className="flex flex-col items-start">
              <span className="text-xs text-blue-100">Connect on</span>
              <span className="font-semibold">LinkedIn</span>
            </div>
          </motion.a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {/* WhatsApp Card */}
        <motion.div
          className="bg-gradient-to-br from-green-900/20 to-green-800/10 rounded-2xl p-8 border border-green-800/30 hover:shadow-lg transition-all"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-green-500 p-4 rounded-full">
              <FaWhatsapp className="text-4xl text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">
              WhatsApp
            </h3>
            <p className="text-gray-300 text-sm">
              Send me a message directly
            </p>
            <p className="text-green-400 font-mono text-lg">
              {profile.whatsapp}
            </p>
            <button
              onClick={handleWhatsApp}
              className="group mt-4 flex items-center gap-2 bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-800 transition-all font-medium"
            >
              Chat on WhatsApp
              <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>

        {/* Email Card */}
        <motion.div
          className="bg-gradient-to-br from-red-900/20 to-red-800/10 rounded-2xl p-8 border border-red-800/30 hover:shadow-lg transition-all"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-red-500 p-4 rounded-full">
              <FaEnvelope className="text-4xl text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">
              Email
            </h3>
            <p className="text-gray-300 text-sm">
              Drop me an email anytime
            </p>
            <p className="text-red-400 font-mono text-sm break-all px-2">
              {profile.email}
            </p>
            <button
              onClick={handleEmail}
              className="group mt-4 flex items-center gap-2 hover:bg-red-800 text-white px-6 py-3 rounded-lg bg-red-700 transition-all font-medium"
            >
              Send an Email
              <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
